import { selectConfiguracionCanal } from '@/store/selectors';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, Renderer2, RendererFactory2, RendererStyleFlags2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { BehaviorSubject, filter, map, Observable } from 'rxjs';
import { ChannelProcessorService } from '../domain';
import { ConfiguracionCanal } from '../models';

export type ThemeType = 'default' | 'test';

@Injectable({
	providedIn: 'root',
})
export class ThemeService {
	private renderer: Renderer2;
	private currentThemeSubject = new BehaviorSubject<ThemeType>('default');
	public currentTheme$: Observable<ThemeType> = this.currentThemeSubject.asObservable();
	private doc = inject(DOCUMENT);
  private platformId = inject(PLATFORM_ID);

	constructor(
		rendererFactory: RendererFactory2,
		private store: Store,
		private channelProcessorService: ChannelProcessorService,
	) {
		this.renderer = rendererFactory.createRenderer(null, null);
	}

	init(): void {
    const savedTheme = (localStorage.getItem('theme') as ThemeType) || 'default';
    this.setTheme(savedTheme);


		if (isPlatformBrowser(this.platformId)) {
			this.store
				.select(selectConfiguracionCanal)
				.pipe(
					map((context: ConfiguracionCanal | null) => this.channelProcessorService.processChannelTheme(context)),
					filter((primaryColor: string | null) => !!primaryColor),
					takeUntilDestroyed()
				).subscribe(primaryColor =>
					this.setStyleColor(primaryColor as string)
				);
		}
  }

	setTheme(theme: ThemeType): void {
		document.documentElement.removeAttribute('data-theme');

		this.renderer.setAttribute(document.documentElement, 'data-theme', theme);

		localStorage.setItem('theme', theme);
		this.currentThemeSubject.next(theme);
	}

	getCurrentTheme(): ThemeType {
		return this.currentThemeSubject.value;
	}

	setStyleColor(color: string): void {
		const root = this.doc.documentElement;
		const color50 = this.calculateColor(color, 0.85);
		const color600 = this.calculateColor(color, -.20);
		const colors: Record<string, string> = {
			'--color-primary-500': color,
			'--background-primary-500': color,
			'--color-primary-600': color600,
			'--background-primary-600': color600,
			'--color-primary-50': color50,
			'--background-primary-50': color50,
		};

		(Object.keys(colors)).map((name) => 
			this.renderer.setStyle(root, name, colors?.[name] ?? '', RendererStyleFlags2.DashCase)
		)
	}

	calculateColor(hex: string, factor: number): string {
		const num = parseInt(hex.replace('#', ''), 16);
		const r = (num >> 16) & 0xff;
		const g = (num >> 8) & 0xff;
		const b = num & 0xff;

		const target = factor >= 0 ? 255 : 0;
		const f = Math.abs(factor);

		const nr = Math.round(r + (target - r) * f);
		const ng = Math.round(g + (target - g) * f);
		const nb = Math.round(b + (target - b) * f);

		return `#${((1 << 24) + (nr << 16) + (ng << 8) + nb).toString(16).slice(1)}`;
	}
}
